<i18n>
{
  "en": {
    "loadDataError": "There was an error loading the data",
    "savingError": "There was an error saving the menu data",
    "outOfMenuError": "Error loading out of menu products. Please, try again later",
    "generatingOutOfMenu": "Generating products out of menu",
    "outOfMenuTitle": "Out of Menu"
  },
  "es": {
    "loadDataError": "Ha habido un error cargando los datos",
    "savingError": "Ha habido un error guardando los datos de menú",
    "outOfMenuError": "Error cargando productos fuera de menú. Inténtelo de nuevo más tarde",
    "generatingOutOfMenu": "Generando productos fuera de menú",
    "outOfMenuTitle": "Fuera de Carta"
  }
}
</i18n>

<template>
  <div class="d-flex flex-column height-100 pb-8">
    <menu-manager-filters ref="menuFilters" :hasChanges="hasChanges" @apply-filters="onApplyFilters"
                          @save="onSave" @out-of-menu="onOutOfMenu" class="mt-1"/>
    <menu-table v-if="!isLoading" :frontMenuProductDynamicData="frontMpddsToFilter"
                @mpdd-order-changed="onMpddOrderChanged"
                @mpdd-special-changed="onMpddSpecialChanged"
                @mpdd-is-visible-changed="onMpddIsVisibleChanged"
                @undo-order="onUndoOrder"
                @undo-special="onUndoSpecial"
                @undo-is-visible="onUndoIsVisible"
                class="flex-grow-1"/>
    <dynameat-skeleton v-else skeletonType="table"/>
    <dynameat-confirmation-modal :visible="outOfMenuLoading"
                                 :title="$t('generatingOutOfMenu')"
                                 :is-loading="outOfMenuLoading"
    />
    <product-kpi-panel v-if="productGuid" :productGuid="productGuid" :product-name="productName"
                       @close="onCloseKpiPanel"/>
  </div>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import * as Sentry from '@sentry/vue';
import ProductKpiPanel from '../../components/ProductKpiPanel.vue';
import { EventBus } from '../../helpers/eventBus';
import { FrontMenuProductDynamicDataList } from './models';
import MenuManagerFilters from './components/MenuManagerFilters.vue';

@Component
@Components({
  MenuTable: () => import('./components/MenuTable.vue'),
  DynameatSkeleton,
  ProductKpiPanel,
  MenuManagerFilters,
  DynameatConfirmationModal
})
@MapActions(['getMenuProductDynamicDataListRequest', 'updateMenuProductDynamicDataListRequest',
  'setLoading', 'applyMenuProductDynamicDataListChanges', 'addNotification', 'outOfMenuRequest'])
@MapGetters(['frontMenuProductDynamicDataList', 'isLoading', 'menuFilterList', 'selectedPointOfSale'])
export default class MenuManager extends Vue {
  mpddOrderChanges = [];

  mpddSpecialChanges = [];

  mpddIsVisibleChanges = [];

  saleRateGuid = '';

  startDate = '';

  endDate = '';

  menuGuidToFilter = '';

  sectionGuidToFilter = '';

  productGuid = null;

  productName = '';

  outOfMenuLoading = false;

  get hasChanges() {
    return (this.mpddOrderChanges.length + this.mpddSpecialChanges.length + this.mpddIsVisibleChanges.length) !== 0;
  }

  get showTable() {
    return this.saleRateGuid && this.endDate && this.startDate && this.menuGuidToFilter && this.sectionGuidToFilter;
  }

  get frontMpddsToFilter() {
    if (this.showTable) {
      const menuProductGuids = (this.menuGuidToFilter) ? this.menuProductGuidsToFilter : [];
      const mpdds = (menuProductGuids.length)
        ? this.frontMenuProductDynamicDataList.menuProductDynamicDataList.filter(
          (mpdd) => menuProductGuids.includes(mpdd.menuProductGuid)
        ) : [];
      return (this.menuGuidToFilter)
        ? new FrontMenuProductDynamicDataList(
          this.frontMenuProductDynamicDataList.saleRateGuid,
          this.frontMenuProductDynamicDataList.startDate,
          this.frontMenuProductDynamicDataList.endDate,
          this.frontMenuProductDynamicDataList.groupList, mpdds
        )
        : this.frontMenuProductDynamicDataList;
    }
    return null;
  }

  get menuProductGuidsToFilter() {
    return (this.sectionToFilter) ? this.sectionToFilter.menuProductGuids : this.menuToFilter?.sections?.flatMap((section) => section.menuProductGuids);
  }

  get menuToFilter() {
    return this.menuFilterList.find((menuFilter) => menuFilter.menuGuid === this.menuGuidToFilter);
  }

  get sectionToFilter() {
    let sectionToFilter = null;
    if (this.sectionGuidToFilter) {
      sectionToFilter = this.menuToFilter?.sections?.find((sectionFilter) => sectionFilter.sectionGuid === this.sectionGuidToFilter);
    }
    return sectionToFilter;
  }

  async created() {
    EventBus.$on('product', this.onProduct);
    await this.loadData();
  }

  mounted() {
    this.mpddOrderChanges = [];
    this.mpddSpecialChanges = [];
    this.mpddIsVisibleChanges = [];
    this.saleRateGuid = '';
    this.startDate = '';
    this.endDate = '';
    this.menuGuidToFilter = '';
    this.sectionGuidToFilter = '';
    this.productGuid = null;
    this.productName = '';
  }

  destroyed() {
    EventBus.$off('product', this.onProduct);
  }

  onProduct(productGuid, productName) {
    this.productGuid = productGuid;
    this.productName = productName;
  }

  async loadData() {
    try {
      if (this.saleRateGuid && this.startDate && this.endDate) {
        await this.setLoading(1);
        await this.getMenuProductDynamicDataListRequest({
          saleRateGuid: this.saleRateGuid,
          startDate: this.startDate,
          endDate: this.endDate
        });
        await this.setLoading(-1);
      }
    } catch (e) {
      await this.setLoading(-1);
      this.addNotification(createErrorNotification(this.$t('loadDataError')));
    }
  }

  onMpddOrderChanged(changes) {
    this.mpddOrderChanges.push(...changes);
  }

  onMpddSpecialChanged(change) {
    this.mpddSpecialChanges.push(change);
  }

  onMpddIsVisibleChanged(change) {
    this.mpddIsVisibleChanges.push(change);
  }

  onCloseKpiPanel() {
    this.productGuid = null;
  }

  async onSave() {
    try {
      await this.setLoading(1);
      await this.applyMenuProductDynamicDataListChanges({
        mpddOrderChanges: this.mpddOrderChanges,
        mpddSpecialChanges: this.mpddSpecialChanges,
        mpddIsVisibleChanges: this.mpddIsVisibleChanges
      });
      await this.updateMenuProductDynamicDataListRequest({ mpddsToUpdate: this.frontMenuProductDynamicDataList.getChangedList() });
      this.mpddOrderChanges = [];
      this.mpddSpecialChanges = [];
      this.mpddIsVisibleChanges = [];
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('savingError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  onOutOfMenu({ startDate, endDate }) {
    const windowRef = window.open();
    this.onOutOfMenuAsync(startDate, endDate, windowRef);
  }

  async onOutOfMenuAsync(startDate, endDate, windowRef) {
    try {
      this.outOfMenuLoading = true;
      const outMenuTitle = this.$t('outOfMenuTitle');
      const outOfMenu = await this.outOfMenuRequest({
        posGuid: this.selectedPointOfSale.guid, startDate, endDate, outMenuTitle
      });
      this.outOfMenuLoading = false;
      outOfMenu.printWindow(windowRef);
    } catch (err) {
      windowRef?.close();
      Sentry.captureException(err);
      this.addNotification(createErrorNotification(this.$t('outOfMenuError'), null, 3000));
    } finally {
      this.outOfMenuLoading = false;
    }
  }

  async onApplyFilters(payload) {
    const {
      dates, menu, section, saleRate
    } = payload;
    this.saleRateGuid = saleRate;
    this.startDate = dates.startDate;
    this.endDate = dates.endDate;
    this.menuGuidToFilter = menu;
    this.sectionGuidToFilter = section;

    this.discardChanges();
    if (this.needToUpdateMpdds()) await this.loadData();
  }

  discardChanges() {
    this.mpddOrderChanges = [];
    this.mpddSpecialChanges = [];
    this.mpddIsVisibleChanges = [];
  }

  needToUpdateMpdds() {
    return !this.frontMenuProductDynamicDataList
      || this.frontMenuProductDynamicDataList.saleRateGuid !== this.saleRateGuid
      || this.frontMenuProductDynamicDataList.startDate !== this.startDate
      || this.frontMenuProductDynamicDataList.endDate !== this.endDate;
  }

  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      const answer = this.$refs.menuFilters.checkRouteLeave();
      next(answer);
    } else next();
  }

  onUndoOrder(numberOfChanges = 1) {
    for (let i = 0; i < numberOfChanges; i++) {
      this.mpddOrderChanges.pop();
    }
  }

  onUndoIsVisible() {
    this.mpddIsVisibleChanges.pop();
  }

  onUndoSpecial() {
    this.mpddSpecialChanges.pop();
  }
}

</script>

<style scoped>
.height-100 {
  height: 100%;
}
</style>
